import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';
import { clsx } from 'clsx';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

function ToastItem({ toast, onRemove }: ToastItemProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(toast.id);
    }, toast.duration || 5000);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />,
    error: <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />,
    warning: <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />,
    info: <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />,
  };

  const bgColors = {
    success: 'bg-white border-l-4 border-green-500 shadow-lg',
    error: 'bg-white border-l-4 border-red-500 shadow-lg',
    warning: 'bg-white border-l-4 border-yellow-500 shadow-lg',
    info: 'bg-white border-l-4 border-blue-500 shadow-lg',
  };

  return (
    <div
      className={clsx(
        'pointer-events-auto overflow-hidden rounded-lg',
        'transform transition-all duration-300 ease-out',
        'animate-in slide-in-from-right-full fade-in-0',
        'hover:scale-105 transition-transform duration-200',
        bgColors[toast.type]
      )}
      style={{
        animation: 'slideInRight 0.3s ease-out, fadeIn 0.3s ease-out',
        minWidth: '320px',
        maxWidth: '400px'
      }}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {icons[toast.type]}
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 leading-tight">
              {toast.title}
            </p>
            {toast.message && (
              <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                {toast.message}
              </p>
            )}
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md p-1 hover:bg-gray-100 transition-colors duration-200"
              onClick={() => onRemove(toast.id)}
            >
              <span className="sr-only">Dismiss</span>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { ...toast, id }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Make addToast available globally
  useEffect(() => {
    (window as any).toast = addToast;
    return () => {
      delete (window as any).toast;
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 pointer-events-none">
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `}</style>
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem
            toast={toast}
            onRemove={removeToast}
          />
        </div>
      ))}
    </div>
  );
}

// Helper functions for easy usage
export const toast = {
  success: (title: string, message?: string, duration?: number) => {
    const addToast = (window as any).toast;
    if (addToast) {
      addToast({ type: 'success', title, message, duration });
    }
  },
  error: (title: string, message?: string, duration?: number) => {
    const addToast = (window as any).toast;
    if (addToast) {
      addToast({ type: 'error', title, message, duration });
    }
  },
  warning: (title: string, message?: string, duration?: number) => {
    const addToast = (window as any).toast;
    if (addToast) {
      addToast({ type: 'warning', title, message, duration });
    }
  },
  info: (title: string, message?: string, duration?: number) => {
    const addToast = (window as any).toast;
    if (addToast) {
      addToast({ type: 'info', title, message, duration });
    }
  },
};
